import type BaseModal from '~/components/Base/BaseModal/BaseModal.vue'

export enum TransitionState {
  Enter,
  Entering,
  Leave,
  Leaving
}

type TransitionListeners = {
  beforeEnter: () => void
  afterEnter: () => void
  beforeLeave: () => void
  afterLeave: () => void
}

function useTransitionState(_visible = false) {
  const visible = shallowRef(_visible)
  const state = shallowRef(visible.value ? TransitionState.Enter : undefined)

  const listeners = {
    beforeEnter: () => { state.value = TransitionState.Entering },
    afterEnter: () => { state.value = TransitionState.Enter },
    beforeLeave: () => { state.value = TransitionState.Leaving },
    afterLeave: () => { state.value = TransitionState.Leave }
  }

  return { visible, state, listeners }
}

export function useTransition(
  props: InstanceType<typeof BaseModal>['$props'],
  options: {
    modelValueLocal: Ref<boolean>
    onEntering?: () => void
    onEnter?: () => void
    onLeaving?: () => void
    onLeave?: () => void
  }
): {
  visible: Ref<boolean>
  contentVisible: Ref<boolean>
  contentListeners: TransitionListeners
  overlayVisible: Ref<boolean>
  overlayListeners: TransitionListeners
  enterTransition: () => Promise<void>
  leaveTransition: () => void
} {
  const { modelValueLocal, onEntering, onEnter, onLeaving, onLeave } = options
  const visible = shallowRef(modelValueLocal.value)

  const { visible: contentVisible, state: contentState, listeners: contentListeners } = useTransitionState(modelValueLocal.value)
  const { visible: overlayVisible, state: overlayState, listeners: overlayListeners } = useTransitionState(modelValueLocal.value)

  const isReadyToBeDestroyed = computed(() => {
    return (props.hideOverlay || overlayState.value === TransitionState.Leave) &&
      contentState.value === TransitionState.Leave
  })

  watch(isReadyToBeDestroyed, (value) => {
    if (value) {
      visible.value = false
    }
  })

  watch(contentState, (state) => {
    switch (state) {
      case TransitionState.Entering:
        if (visible.value && onEntering) {
          onEntering()
        }
        break

      case TransitionState.Enter:
        if (visible.value && onEnter) {
          onEnter()
        }
        break

      case TransitionState.Leaving:
        onLeaving?.()
        break

      case TransitionState.Leave:
        onLeave?.()
        break
    }
  })

  async function enterTransition() {
    visible.value = true
    await nextTick()
    contentVisible.value = true
    overlayVisible.value = true
  }

  function leaveTransition() {
    contentVisible.value = false
    overlayVisible.value = false
  }

  return {
    visible,

    contentVisible,
    contentListeners,

    overlayVisible,
    overlayListeners,

    enterTransition,
    leaveTransition
  }
}
